import type { VoiceEventDefinition } from '../../content/voices/voice-events.js';

/** Clip urls by event id, for one voice pack. */
export type VoiceClips = Map<string, string[]>;

/** `change target 2.mp3` answers `change-target`: the file name without its number and extension, lower-cased, spaces as dashes. */
export function clipEventId(fileName: string): string {
  return fileName.replace(/\.[a-z0-9]+$/i, '').replace(/[\s_-]*\d+$/, '').trim().toLowerCase().replace(/[\s_]+/g, '-');
}

/** Groups `{ "…/hero voices/<folder>/<clip>": url }` into the clips of each pack folder. */
export function groupClips(files: Record<string, string>): Map<string, VoiceClips> {
  const folders = new Map<string, VoiceClips>();
  for (const [path, url] of Object.entries(files)) {
    const parts = path.split('/'), file = parts.pop(), folder = parts.pop();
    if (!file || !folder) continue;
    const clips = folders.get(folder) ?? new Map<string, string[]>(), id = clipEventId(file);
    clips.set(id, [...(clips.get(id) ?? []), url]); folders.set(folder, clips);
  }
  return folders;
}

/** Where a voice comes from: `strength` (0..1) is how loud it is for the listener and `pan` (-1 left to 1 right) which side of the screen it is on. */
export interface VoicePlacement { strength: number; pan: number }

/** Footfalls fade to nothing 34 m from the camera; a voice is feedback for an order, so it carries further and never falls below a murmur. */
const VOICE_REACH = 70, VOICE_FLOOR = .2, VOICE_PAN = .7;
/** A hero `distance` metres from the camera's focus, `screenX` (-1 left edge to 1 right edge) across the screen. */
export function placeVoice(distance: number, screenX: number): VoicePlacement {
  // A camera projected before its matrices settle (a frame rendered with no time elapsed) gives NaN, which an AudioParam rejects.
  if (!Number.isFinite(distance)) distance = 0;
  if (!Number.isFinite(screenX)) screenX = 0;
  return { strength: Math.max(VOICE_FLOOR, 1 - Math.max(0, distance) / VOICE_REACH), pan: Math.max(-VOICE_PAN, Math.min(VOICE_PAN, screenX * VOICE_PAN)) };
}

/**
 * Combat effects are at full strength within a few metres of the camera's focus, then fall away so the clash in view stands out from
 * one across the field (about three quarters as loud at 20 m, a little over half at 30 m), and are gone at 60 m.
 */
const SOUND_REACH = 60, SOUND_NEAR = 12, SOUND_HALVING = 50;
/** Where a world sound comes from, `distance` metres from the camera's focus and `screenX` across the screen; panned as voices are. */
export function placeSound(distance: number, screenX: number): VoicePlacement {
  const { pan } = placeVoice(distance, screenX), away = Number.isFinite(distance) ? Math.max(0, distance) / SOUND_REACH : 0;
  const beyond = Number.isFinite(distance) ? Math.max(0, distance - SOUND_NEAR) : 0;
  return { strength: Math.max(0, 1 - away * away) / (1 + beyond / SOUND_HALVING), pan };
}

export interface VoiceDirectorOptions {
  events: VoiceEventDefinition[];
  /** The clips of a pack, or nothing when it is unknown. */
  clips: (packId: string) => VoiceClips | undefined;
  /** Starts a clip, placed where the speaker is if known, and returns how many seconds it lasts, or 0 when nothing played. */
  play: (url: string, placement?: VoicePlacement, heroId?: string, eventId?: string) => number;
  stop?: () => void;
  /** Where a hero is heard from; absent or null plays the line at full strength in the middle. */
  place?: (heroId: string) => VoicePlacement | null;
  /** The player's voice frequency in percent: 100 keeps each event's own odds, 0 silences every hero. */
  frequency: () => number;
  /** Whether this hero is able to speak right now (not fallen). */
  canSpeak?: (heroId: string) => boolean;
  random?: () => number;
  /** Seconds on any steady clock. */
  now?: () => number;
}

/**
 * Decides when a hero speaks. A trigger names the heroes it concerns and an event; the director rolls the event's odds (scaled by the
 * player's voice frequency), picks one hero who is allowed to answer, and plays the next clip of that hero's pack for the event. Clips
 * are dealt from a shuffled bag so a line never repeats before the rest have been heard.
 */
export class VoiceDirector {
  private readonly events: Map<string, VoiceEventDefinition>;
  private readonly packs = new Map<string, string | null>();
  private readonly lastSpoken = new Map<string, number>();
  private readonly bags = new Map<string, string[]>();
  private readonly lastClip = new Map<string, string>();
  private readonly overrides = new Map<string, number>();
  private busyUntil = 0;
  private busyPriority = -Infinity;
  private readonly random: () => number;
  private readonly now: () => number;

  constructor(private readonly options: VoiceDirectorOptions) {
    this.events = new Map(options.events.map(event => [event.id, event]));
    this.random = options.random ?? Math.random;
    this.now = options.now ?? (() => performance.now() / 1000);
  }

  /** Sets which pack a hero speaks with; `null` silences the hero. */
  assign(heroId: string, packId: string | null) { this.packs.set(heroId, packId); }

  /** Replaces the odds of an event for this visit (0 to 1); `null` goes back to the event's own. */
  setChance(eventId: string, chance: number | null) {
    if (chance === null) this.overrides.delete(eventId); else this.overrides.set(eventId, Math.min(1, Math.max(0, chance)));
  }

  /** The odds that one trigger of the event is spoken right now. */
  chance(eventId: string, auto = false): number {
    const event = this.events.get(eventId);
    if (!event) return 0;
    return Math.min(1, Math.max(0, (this.overrides.get(eventId) ?? (auto ? event.autoChance : undefined) ?? event.chance) * this.options.frequency() / 100));
  }

  /** The heroes (of those named) that could answer this event now. */
  private candidates(heroIds: string[], event: VoiceEventDefinition, now: number, force: boolean) {
    return heroIds.filter(id => {
      const pack = this.packs.get(id);
      return pack && this.options.clips(pack)?.get(event.id)?.length && (this.options.canSpeak?.(id) ?? true)
        && (force || now - (this.lastSpoken.get(`${id}:${event.id}`) ?? -Infinity) >= event.cooldown);
    });
  }

  /** Returns whether a hero spoke. `force` skips the odds, the cooldown and any line still playing, to hear an event on demand. `auto` rolls the event's odds for a hero acting on their own. */
  say(heroIds: string[], eventId: string, { force = false, auto = false } = {}): boolean {
    const event = this.events.get(eventId), now = this.now();
    if (!event || !force && now < this.busyUntil && (event.priority < this.busyPriority || event.priority === this.busyPriority && !event.interruptEqual)) return false;
    const able = this.candidates(heroIds, event, now, force);
    if (!able.length || !force && this.random() >= this.chance(eventId, auto)) return false;
    const hero = able[Math.floor(this.random() * able.length)], pack = this.packs.get(hero)!;
    const url = this.nextClip(pack, eventId, this.options.clips(pack)!.get(eventId)!);
    this.options.stop?.();
    const length = this.options.play(url, this.options.place?.(hero) ?? undefined, hero, eventId);
    if (!length) return false;
    this.lastSpoken.set(`${hero}:${eventId}`, now);
    this.busyUntil = now + length; this.busyPriority = event.priority;
    return true;
  }

  /** Plays a line of a pack's regardless of any hero, odds or cooldown: the voice picker's sample. */
  sample(packId: string, eventId = 'selection'): boolean {
    const clips = this.options.clips(packId)?.get(eventId);
    return !!clips?.length && this.options.play(this.nextClip(packId, eventId, clips), undefined, undefined, eventId) > 0;
  }

  /** Forgets who spoke when and what is playing, for a new room. Assignments and odds stay. */
  reset() { this.options.stop?.(); this.lastSpoken.clear(); this.busyUntil = 0; this.busyPriority = -Infinity; }

  private nextClip(pack: string, eventId: string, clips: string[]): string {
    const key = `${pack}:${eventId}`;
    let bag = this.bags.get(key);
    if (!bag?.length) {
      bag = [...clips];
      for (let i = bag.length - 1; i > 0; i--) { const j = Math.floor(this.random() * (i + 1)); [bag[i], bag[j]] = [bag[j], bag[i]]; }
      // A fresh bag never opens with the line the last one ended on.
      const last = this.lastClip.get(key);
      if (bag.length > 1 && bag.at(-1) === last) [bag[0], bag[bag.length - 1]] = [bag[bag.length - 1], bag[0]];
      this.bags.set(key, bag);
    }
    const url = bag.pop()!;
    this.lastClip.set(key, url);
    return url;
  }
}
